/**
 * UI Components - Moon Moon Agency
 * Handles read more functionality, loader states, and UI components
 */

/*********************************************************************************************
 * 🚀 LOADER MODULE
 */
const LoaderManager = {
	// Local configuration
	config: {
		classes: {
			loading: 'mm-loading',
			active: 'mm-active',
			expanded: 'mm-expanded'
		},
		selectors: {
			readMoreToggle: '[data-read-more-toggle]',
			readMoreContainer: '[data-read-more]',
			readMoreText: '[data-read-more-text]',
			readMoreLabelShow: '[data-read-more-show]',
			readMoreLabelHide: '[data-read-more-hide]'
		}
	},

	/**
	 * Add loading state to specified element
	 */
	loadThis(elem) {
		if (!elem) {
			window.log('warn', 'LoaderManager', 'No element provided');
			return;
		}
		elem.classList.add(this.config.classes.loading);
		window.emit('global:loader-activated', { element: elem.tagName });
	}
};

/*********************************************************************************************
 * 📖 READ MORE MODULE
 */
const ReadMoreManager = {
	/**
	 * Toggle read more state
	 */
	toggle(btn) {
		const container = btn.closest(LoaderManager.config.selectors.readMoreContainer);
		if (!container) return;

		const textElement = container.querySelector(LoaderManager.config.selectors.readMoreText);
		const labelShow = btn.querySelector(LoaderManager.config.selectors.readMoreLabelShow);
		const labelHide = btn.querySelector(LoaderManager.config.selectors.readMoreLabelHide);

		if (!textElement || !labelShow || !labelHide) return;

		const expanded = btn.getAttribute('aria-expanded') === 'true';
		const newState = !expanded;

		// Update state
		textElement.classList.toggle(LoaderManager.config.classes.expanded, newState);
		btn.setAttribute('aria-expanded', newState.toString());
		labelShow.hidden = newState;
		labelHide.hidden = !newState;

		window.emit('global:read-more-toggled', { expanded: newState });
	},

	/**
	 * Initialize read more functionality
	 */
	init() {
		const toggleButtons = window.getElements(LoaderManager.config.selectors.readMoreToggle);
		if (!toggleButtons.length) return;

		toggleButtons.forEach((btn) => {
			// Initialize aria-expanded if not present
			if (!btn.hasAttribute('aria-expanded')) {
				btn.setAttribute('aria-expanded', 'false');
			}

			// Click event
			btn.addEventListener('click', () => {
				this.toggle(btn);
			});

			// Keyboard support (Enter/Space)
			btn.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.toggle(btn);
				}
			});
		});
	}
};

/*********************************************************************************************
 * 🔗 EXTERNAL LINKS MODULE
 */
const ExternalLinksManager = {
	/**
	 * Process external links to open in new tab
	 */
	init() {
		const links = window.getElements('a:not(.consent-wrapper)');
		let processedCount = 0;

		links.forEach((link) => {
			if (link.hostname !== window.location.hostname) {
				link.target = '_blank';
				link.rel = 'noreferrer noopener';
				processedCount++;
			}
		});

	}
};

/*********************************************************************************************
 * 📏 DOCUMENT HEIGHT MODULE
 */
const DocumentHeightManager = {
	/**
	 * Update document height CSS variable
	 */
	update() {
		const newHeight = `${window.innerHeight}px`;
		document.documentElement.style.setProperty('--doc-height', newHeight);
		window.emit('global:document-height-updated', { height: newHeight });
	}
};

/*********************************************************************************************
 * 🎠 SPLIDE MODULE
 */
const SplideManager = {
	/**
	 * Initialize all Splide carousels with their data-splide configurations
	 */
	init() {
		const splideElements = window.getElements('.splide');
		if (!splideElements.length || typeof Splide === 'undefined') return;

		let initializedCount = 0;
		const sectionsWithSplide = [];

		splideElements.forEach(element => {
			// Skip already initialized sliders
			if (element._splide) return;

			// Skip product media sliders - they're synced and initialized in mm-product-medias.liquid
			const isProductMediaSlider = element.hasAttribute('data-main-slider') ||
										element.hasAttribute('data-thumbnail-slider');
			if (isProductMediaSlider) return;

			try {
				const configData = element.dataset.splide;
				let splideInstance;

				if (configData) {
					try {
						const config = JSON.parse(configData);
						splideInstance = new Splide(element, config).mount();
					} catch (jsonError) {
						window.log('warn', 'SplideManager', `Invalid JSON in data-splide, using defaults: ${element.className}`);
						splideInstance = new Splide(element).mount();
					}
				} else {
					// Configuration par défaut si pas de data-splide
					splideInstance = new Splide(element).mount();
				}

				// Exposer l'instance sur l'element : active la garde anti-double-init
				// (ligne ~149) et permet un refresh() externe (ex: reveal d'un panneau masque).
				element._splide = splideInstance;

				// Gérer la progress bar si elle existe
				const progressBar = element.querySelector('.splide__progress__bar');
				if (progressBar && splideInstance) {
					const updateProgress = function() {
						try {
							const end = splideInstance.Components.Controller.getEnd() + 1;
							const rate = Math.min((splideInstance.index + 1) / end, 1);
							progressBar.style.width = String(100 * rate) + '%';
						} catch (error) {
							window.log('error', 'SplideManager', 'Progress bar update failed');
						}
					};

					// Initialiser immédiatement et au montage
					splideInstance.on('mounted ready', updateProgress);
					splideInstance.on('move moved', updateProgress);

					// Force l'initialisation après un court délai
					requestAnimationFrame(() => {
						setTimeout(updateProgress, 50);
					});
				}

				// Masquer les arrows si tous les slides sont visibles
				const arrowsEl = element.querySelector('.splide__arrows');
				if (arrowsEl) {
					const toggleArrows = () => {
						const overflow = splideInstance.Components.Layout.isOverflow();
						arrowsEl.style.visibility = overflow ? '' : 'hidden';
					};
					splideInstance.on('mounted resize', toggleArrows);
				}

				// Scroll horizontal trackpad / molette inclinable -> navigation slide
				this._bindWheelNav(splideInstance);

				// Collecter les sections
				const sectionElement = element.closest('section');
				if (sectionElement) {
					const sectionName = sectionElement.className || 'unnamed-section';
					if (!sectionsWithSplide.includes(sectionName)) {
						sectionsWithSplide.push(sectionName);
					}
				}

				initializedCount++;

			} catch (error) {
				window.log('error', 'SplideManager', `Initialization failed for element: ${element.className}`);

				// Initialiser avec config par défaut en cas d'erreur
				try {
					element._splide = new Splide(element).mount();
					initializedCount++;
				} catch (fallbackError) {
					window.log('error', 'SplideManager', `Fallback initialization failed: ${fallbackError.message}`);
				}
			}
		});

		// Émettre un seul événement global avec la liste des sections
		if (initializedCount > 0) {
			window.emit('global:splide-initialized', {
				count: initializedCount,
				sections: sectionsWithSplide
			});
		}
	},

	/**
	 * Rendre un slider navigable au scroll horizontal (trackpad 2 doigts,
	 * molette inclinable, Shift+molette). Splide gere deja le drag souris/tactile,
	 * mais ignore le `wheel` horizontal : on le branche ici sur le track.
	 *
	 * @param {object} splideInstance - Instance Splide montee
	 */
	_bindWheelNav(splideInstance) {
		const track = splideInstance.Components.Elements.track;
		if (!track) return;

		// Throttle : le trackpad emet des dizaines d'events par geste.
		// On limite a un `go()` par intervalle pour ne pas sauter plusieurs slides.
		let lastMove = 0;
		const THROTTLE = 400;

		const onWheel = (e) => {
			// Intention horizontale uniquement : ne pas capturer le scroll vertical de la page
			if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

			// Slider sans overflow (tous les slides visibles) : laisser passer le scroll
			if (!splideInstance.Components.Layout.isOverflow()) return;

			// On agit horizontalement : empecher le geste back/forward du navigateur
			if (e.cancelable) e.preventDefault();

			const now = e.timeStamp;
			if (now - lastMove < THROTTLE) return;
			lastMove = now;

			splideInstance.go(e.deltaX > 0 ? '>' : '<');
		};

		// passive: false requis pour pouvoir preventDefault le geste horizontal
		track.addEventListener('wheel', onWheel, { passive: false });

		// Nettoyage si l'instance est detruite (re-render editeur de theme)
		splideInstance.on('destroy', () => {
			track.removeEventListener('wheel', onWheel, { passive: false });
		});
	}
};

/*********************************************************************************************
 * 🔻 DETAILS ACCORDION MODULE
 *
 * Anime l'ouverture/fermeture des <details> de maniere cross-browser.
 * Le CSS ::details-content (block-size 0 -> auto) n'anime que sur Chrome
 * (interpolate-size non supporte par Safari/Firefox) : on anime ici la hauteur
 * du wrapper de contenu en pixels via Web Animations API, interpolee partout.
 *
 * Pre-requis markup : chaque <details> a un seul element wrapper apres son <summary>.
 */
const DetailsAccordionManager = {
	config: {
		selector: 'details',
		// Aligne sur --duration-fast (mm-variables.css) : une seule source de
		// verite pour la duree, partagee avec les transitions CSS.
		get duration() {
			const raw = getComputedStyle(document.documentElement).getPropertyValue('--duration-fast');
			return parseFloat(raw) || 300;
		},
		easing: 'ease',
	},

	init() {
		// Delegation : un seul listener pour tous les <details> (y compris ceux
		// injectes apres coup, ex: panneau filtres ouvert dynamiquement).
		document.addEventListener('click', (e) => {
			const summary = e.target.closest('summary');
			if (!summary) return;
			const details = summary.parentElement;
			if (!details || details.tagName !== 'DETAILS') return;
			if (!details.matches(this.config.selector)) return;
			this._toggle(e, details);
		});
		window.log('info', '🔻 [DETAILS]', 'Initialized');
	},

	/**
	 * Le wrapper anime = unique enfant direct du <details> hors <summary>.
	 */
	_getContent(details) {
		return [...details.children].find((c) => c !== details.querySelector(':scope > summary'));
	},

	/**
	 * Accordeon exclusif (un seul ouvert) — opt-in via data-mm-accordion-group.
	 *
	 * On n'utilise pas l'attribut natif name= : le navigateur y referme le frere
	 * lui-meme, sans passer par le clic, donc hors du chemin anime ci-dessous.
	 *
	 * @param {HTMLDetailsElement} details - Celui qui va s'ouvrir
	 */
	_closeGroupSiblings(details) {
		const group = details.dataset.mmAccordionGroup;
		if (!group) return; // Comportement par defaut : accordeons independants

		const siblings = window.getElements(`details[data-mm-accordion-group="${group}"]`);
		siblings.forEach((sibling) => {
			if (sibling === details || !sibling.open || sibling._mmAnimating) return;

			const content = this._getContent(sibling);
			if (!content) {
				sibling.open = false;
				sibling.dataset.mmOpen = 'false';
				return;
			}

			sibling._mmAnimating = true;
			sibling.dataset.mmOpen = 'false'; // synchro immediate de l'indicateur
			this._animate(sibling, content, content.scrollHeight, 0, () => {
				sibling.open = false;
			});
		});
	},

	_toggle(e, details) {
		// Respect prefers-reduced-motion : toggle natif instantane.
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		const content = this._getContent(details);
		if (!content) return;

		e.preventDefault(); // on gere le toggle nous-memes

		// Une animation en cours sur ce details : clic absorbe (pas de toggle natif parasite
		// qui desynchroniserait l'etat open pendant l'animation de 750ms).
		if (details._mmAnimating) return;

		const isOpen = details.open;
		details._mmAnimating = true;

		// Etat cible expose des le clic : [open] n'est retire qu'a la FIN de
		// l'animation de fermeture, un indicateur (chevron, +/-) qui s'appuie
		// dessus pivoterait donc avec un cran de retard. Cibler
		// [data-mm-open="false"] en CSS pour rester synchrone.
		details.dataset.mmOpen = String(!isOpen);

		if (!isOpen) {
			// Accordeon exclusif : referme les freres du meme groupe.
			this._closeGroupSiblings(details);

			// OUVERTURE : open d'abord pour que le contenu soit mesurable.
			details.open = true;
			const target = content.scrollHeight;
			this._animate(details, content, 0, target);
		} else {
			// FERMETURE : du courant vers 0, puis open=false a la fin.
			const start = content.scrollHeight;
			this._animate(details, content, start, 0, () => {
				details.open = false;
			});
		}
	},

	_animate(details, content, from, to, onFinish) {
		// overflow:hidden pendant l'anim pour clipper le contenu.
		const prevOverflow = content.style.overflow;
		content.style.overflow = 'hidden';

		const anim = content.animate(
			[{ blockSize: `${from}px` }, { blockSize: `${to}px` }],
			{ duration: this.config.duration, easing: this.config.easing }
		);

		anim.onfinish = () => {
			content.style.overflow = prevOverflow;
			details._mmAnimating = false;
			if (onFinish) onFinish();
		};
		anim.oncancel = () => {
			content.style.overflow = prevOverflow;
			details._mmAnimating = false;
		};
	},
};

/*********************************************************************************************
 * 🎯 UI COMPONENTS MANAGER
 */
const UIComponentsManager = {
	/**
	 * Initialize all UI components
	 */
	init() {
		ReadMoreManager.init();
		ExternalLinksManager.init();
		SplideManager.init();
		DetailsAccordionManager.init();
		DocumentHeightManager.update();
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		DocumentHeightManager.update();
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.LoaderManager = LoaderManager;
window.ReadMoreManager = ReadMoreManager;
window.ExternalLinksManager = ExternalLinksManager;
window.SplideManager = SplideManager;
window.DetailsAccordionManager = DetailsAccordionManager;
window.DocumentHeightManager = DocumentHeightManager;
window.UIComponentsManager = UIComponentsManager;

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	UIComponentsManager.init();
});

// Re-init quand l'editeur de theme (re)charge une section : SplideManager est idempotent
// (garde `if (element._splide) return`), donc pas de double-mount des carousels existants.
document.addEventListener('shopify:section:load', () => UIComponentsManager.init());

window.addEventListener('resize', window.debounce(() => UIComponentsManager.onResize(), 150));
